/**
 * Markdown Library - Parsers
 * 
 * Pure functions for parsing markdown content.
 */

import type { ParsedTable, ContentPart, ColumnAlignment } from './types';
import { MARKDOWN_TABLE_PATTERN } from './patterns';

// =============================================================================
// Table Parsing
// =============================================================================

/**
 * Parses a markdown table string into a structured object.
 * 
 * @param content - Raw markdown table string
 * @returns Parsed table with headers, alignments, and rows
 */
export function parseMarkdownTable(content: string): ParsedTable {
    const lines = content.trim().split('\n');

    // Minimum: header line, separator line, at least one data line
    if (lines.length < 3) {
        return { headers: [], alignments: [], rows: [] };
    }

    const headerLine = lines[0];
    const separatorLine = lines[1];
    const dataLines = lines.slice(2);

    // Parse headers
    const headers = parsePipeDelimitedLine(headerLine);

    // Parse alignments from separator
    const alignments = parseAlignments(separatorLine, headers.length);

    // Parse data rows
    const rows = dataLines
        .map(line => parsePipeDelimitedLine(line))
        .filter(row => row.length > 0);

    return { headers, alignments, rows };
}

/**
 * Parses a pipe-delimited line into an array of cells.
 * Handles leading/trailing pipes and trims whitespace.
 * 
 * @param line - Pipe-delimited line
 * @returns Array of cell values
 */
function parsePipeDelimitedLine(line: string): string[] {
    return line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell !== '');
}

/**
 * Parses alignment hints from the separator line.
 * 
 * Examples:
 * - `---`   -> left
 * - `:---`  -> left
 * - `---:`  -> right
 * - `:---:` -> center
 * 
 * @param separatorLine - Separator line with dashes and colons
 * @param headerCount - Number of headers (to limit alignments)
 * @returns Array of alignment values
 */
function parseAlignments(separatorLine: string, headerCount: number): ColumnAlignment[] {
    return separatorLine
        .split('|')
        .map(sep => {
            const trimmed = sep.trim();
            if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center';
            if (trimmed.endsWith(':')) return 'right';
            return 'left';
        })
        .filter((_, i) => i < headerCount) as ColumnAlignment[];
}

// =============================================================================
// Content Splitting
// =============================================================================

/**
 * Splits markdown content into an array of text and table parts.
 * This allows tables to be rendered with a specialized component
 * while the rest uses standard markdown rendering.
 * 
 * @param content - Full markdown content
 * @returns Array of content parts
 */
export function splitContentByTables(content: string): ContentPart[] {
    const parts: ContentPart[] = [];
    const regex = new RegExp(MARKDOWN_TABLE_PATTERN.source, 'g');

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(content)) !== null) {
        // Add text before the table
        if (match.index > lastIndex) {
            const textBefore = content.substring(lastIndex, match.index);
            if (textBefore.trim()) {
                parts.push({ type: 'text', content: textBefore });
            }
        }

        // Add the table
        parts.push({ type: 'table', content: match[0] });

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after the last table
    if (lastIndex < content.length) {
        const textAfter = content.substring(lastIndex);
        if (textAfter.trim()) {
            parts.push({ type: 'text', content: textAfter });
        }
    }

    return parts;
}

/**
 * Checks if content contains markdown tables.
 * 
 * @param content - Markdown content
 * @returns True if tables are present
 */
export function hasMarkdownTables(content: string): boolean {
    const regex = new RegExp(MARKDOWN_TABLE_PATTERN.source);
    return regex.test(content);
}
